import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import SideNav from "../../components/sidenav/SideNav";
import ChatBox from "../../components/chatbox/ChatBox";
import BrowseTab from "../../components/browse/BrowseTab";
import CollectionsTab from "../../components/collections/CollectionsTab";
import HowToTab from "../../components/howto/HowToTab";
import Navbar from "../../components/Navbar";
import { useAuth } from "../../contexts/AuthContext";
import { useSavedDiscourses } from "../../contexts/SavedDiscoursesContext";
import { apiRoute } from "../../helpers/apiRoute";
// import Feedback from "../../components/feedback/Feedback";

const Chatpage = () => {
  const [newChat, setNewChat] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  // Tab state lives in the URL (?tab=...) so Collections deep links
  // (/home?tab=collections&book=...) and the browser back button work.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "chat"; // "chat" | "browse" | "collections" | "howto"
  const initialChatCreatedRef = useRef(false);
  const { user } = useAuth();

  const handleTabSelect = (key) => {
    // Switching tabs drops collection params — they only apply within Collections.
    setSearchParams(key === "chat" ? {} : { tab: key }, { replace: true });
  };

  // Use saved discourses from context
  const {
    savedDiscourses,
    saveDiscourse,
    removeBookmark,
    loadSavedDiscourses
  } = useSavedDiscourses();

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const closeSidebar = () => {
    setSidebarVisible(false);
  };

  // Reload saved discourses when component mounts or comes into view
  useEffect(() => {
    if (user && user.token) {
      loadSavedDiscourses();
    }
  }, [user, loadSavedDiscourses]);

  // Save a discourse - now using context
  const handleSaveDiscourse = async (discourseData, questionContext) => {
    await saveDiscourse(discourseData, questionContext);
  };

  // Unsave a discourse - now using context  
  const handleUnsaveDiscourse = async (discourseId) => {
    await removeBookmark(discourseId);
  };

  // Load user's chat threads from backend
  const loadUserChats = async () => {
    if (!user || !user.token) return;

    try {
      setLoading(true);
      const response = await fetch(apiRoute(`chats/${user.email}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const chatThreads = await response.json();
        setThreads(chatThreads);

        // If user has existing chats, select the most recent one. Otherwise
        // create and select an empty chat so there is always a valid
        // selectedThreadId before the user's first question (prevents the
        // loadMessages effect from wiping the first answer).
        if (chatThreads.length > 0) {
          setThreads(chatThreads);
          setSelectedThreadId(chatThreads[0].id);
        } else {
          const newThread = await createNewChatThread();
          setSelectedThreadId(newThread.id);
          addThread(newThread);
        }
      } else {
        console.error("Failed to load chats:", response.statusText);
      }
    } catch (error) {
      console.error("Error loading chats:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadThreadMessages = async (threadId) => {
    if (!user || !user.token || !threadId) return [];

    try {
      const response = await fetch(apiRoute(`chats/${threadId}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const thread = await response.json();
        return thread.messages || [];
      }
    } catch (error) {
      console.error("Error loading thread messages:", error);
    }

    return [];
  };

  const ensureActiveThread = async () => {
    if (selectedThreadId) {
      const existing = threads.find((t) => t.id === selectedThreadId);
      if (existing) return existing;
    }

    const emptyChat = threads.find(
      (thread) => !thread.messages || thread.messages.length === 0
    );
    if (emptyChat) {
      setSelectedThreadId(emptyChat.id);
      return emptyChat;
    }

    const newThread = await createNewChatThread();
    setSelectedThreadId(newThread.id);
    setThreads((prev) => [newThread, ...prev]);
    return newThread;
  };

  // Create a new chat thread in backend
  const createNewChatThread = async (title = "New Chat") => {
    if (!user || !user.token) {
      const newThreadId = new Date().toISOString();
      return {
        id: newThreadId,
        title: title,
        timestamp: new Date(),
        messages: [],
      };
    }

    try {
      const requestBody = { title: title };

      if (user.email) {
        requestBody.user_email = user.email;
      }

      const response = await fetch(apiRoute(`chats/${user.email}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const newThread = await response.json();
        return newThread;
      }

      console.error("Failed to create chat thread:", response.statusText);
      const newThreadId = new Date().toISOString();
      return {
        id: newThreadId,
        title: title,
        timestamp: new Date(),
        messages: [],
      };
    } catch (error) {
      console.error("Error creating chat thread:", error);
      const newThreadId = new Date().toISOString();
      return {
        id: newThreadId,
        title: title,
        timestamp: new Date(),
        messages: [],
      };
    }
  };

  // Load user chats when user logs in or out
  useEffect(() => {
    if (user && user.token) {
      loadUserChats();
    } else {
      setThreads([]);
      setSelectedThreadId(null);
    }
  }, [user]);

  // Create initial chat if no threads exist and user is not logged in
  useEffect(() => {
    if (threads.length === 0 && !user && !initialChatCreatedRef.current) {
      const createInitialChat = async () => {
        initialChatCreatedRef.current = true;
        const newThread = await createNewChatThread();
        setSelectedThreadId(newThread.id);
        addThread(newThread);
      };
      createInitialChat();
    }
  }, [threads.length, user]);

  // Save chat thread to backend
  const saveChatThread = async (thread) => {
    if (!user || !user.token) return;

    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(thread.id);
    if (!isUuid) {
      console.error("Cannot save chat thread: invalid thread id", thread.id);
      return;
    }

    try {
      const response = await fetch(apiRoute(`chats/${thread.id}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
        },
        body: JSON.stringify({
          title: thread.title,
          messages: thread.messages,
          user_email: user.email
        })
      });

      if (!response.ok) {
        console.error("Failed to save chat thread:", response.statusText);
      }
    } catch (error) {
      console.error("Error saving chat thread:", error);
    }
  };

  // Delete chat thread from backend
  const deleteChatThread = async (threadId) => {
    if (!user || !user.token) return; // Don't delete if not logged in

    try {
      const response = await fetch(apiRoute(`chats/${threadId}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
        },
        body: JSON.stringify({
          user_email: user.email
        })
      });

      if (!response.ok) {
        console.error("Failed to delete chat thread:", response.statusText);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error deleting chat thread:", error);
      return false;
    }
  };

  // Handle chat deletion
  const handleDeleteChat = async (threadId) => {
    // Remove from local state immediately for better UX
    setThreads((prevThreads) => prevThreads.filter((thread) => thread.id !== threadId));

    // If this was the selected thread, clear selection
    if (selectedThreadId === threadId) {
      setSelectedThreadId(null);
    }

    // Delete from backend if user is logged in
    if (user && user.token) {
      const success = await deleteChatThread(threadId);
      if (!success) {
        // If backend deletion failed, reload threads to restore the deleted one
        loadUserChats();
      }
    }
  };

/*
  // Generate AI summary for chat title from the first question (Disabled for now - using simple truncation instead)
  const generateAITitleFromQuestion = async (question) => {
    if (!question) return "New Chat";

    try {
      const response = await fetch(apiRoute("summarize-question"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: question })
      });

      if (response.ok) {
        const data = await response.json();
        return data.summary;
      } else {
        console.error("Failed to generate AI title:", response.statusText);
        return "AI Generated Title"; // Placeholder - replace with actual backend call
      }
    } catch (error) {
      console.error("Error generating AI title:", error);
      // Fallback to simple truncation if AI generation fails
      const cleanQuestion = question.trim();
      if (cleanQuestion.length <= 50) {
        return cleanQuestion;
      }
      return cleanQuestion.substring(0, 50) + "...";
    }
  };
*/

  const getTitleFromQuestion = (question) => {
    if (!question) return "New Chat";
    const clean = question.trim();
    return clean.length <= 50 ? clean : clean.substring(0, 50) + "...";
  };

  const handleNewChat = async () => {
    // Always return to the Questions tab so ChatBox is mounted and the
    // empty chat window is visible (sidebar can be used from any tab).
    handleTabSelect("chat");

    // Check if there's already an empty chat
    const emptyChat = threads.find(thread => !thread.messages || thread.messages.length === 0);

    if (emptyChat) {
      // If there's an empty chat, just select it
      setSelectedThreadId(emptyChat.id);
      setNewChat(Math.random()); // Trigger new chat in ChatBox
    } else {
      // If no empty chat exists, create a new one
      const newThread = await createNewChatThread();
      setSelectedThreadId(newThread.id);
      addThread(newThread);
      setNewChat(Math.random()); // Trigger new chat in ChatBox
    }
    initialChatCreatedRef.current = true; // Set flag to prevent automatic creation
  };

  // Check if a chat is empty (no messages)
  const isChatEmpty = (threadId) => {
    const thread = threads.find(t => t.id === threadId);
    return !thread || !thread.messages || thread.messages.length === 0;
  };

  // Delete empty chat
  const deleteEmptyChat = async (threadId) => {
    if (isChatEmpty(threadId)) {
      await handleDeleteChat(threadId);
    }
  };

  const handleChatSelect = async (threadId) => {
    // If we're switching away from a chat and it's empty, delete it
    if (selectedThreadId && selectedThreadId !== threadId) {
      await deleteEmptyChat(selectedThreadId);
    }
    setSelectedThreadId(threadId);
  };

  const addThread = (thread) => {
    setThreads((prevThreads) => {
      const existingThread = prevThreads.find((t) => t.id === thread.id);
      if (existingThread) {
        const updatedThreads = prevThreads.map((t) =>
          t.id === thread.id ? { ...t, ...thread } : t,
        );
        // Save to backend if user is logged in
        if (user && user.token) {
          saveChatThread(thread);
        }
        return updatedThreads;
      } else {
        const newThreads = [thread, ...prevThreads];
        // Save to backend if user is logged in
        if (user && user.token) {
          saveChatThread(thread);
        }
        return newThreads;
      }
    });
  };

  // Cleanup empty chats when leaving the page
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (selectedThreadId && isChatEmpty(selectedThreadId)) {
        await deleteEmptyChat(selectedThreadId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [selectedThreadId]);

  const navTabs = [
    { key: "chat", label: "Questions", shortLabel: "Questions" },
    { key: "collections", label: "Collections", shortLabel: "Collections" },
    { key: "browse", label: "Saved Discourses", shortLabel: "Saved" },
    { key: "howto", label: "How to Use", shortLabel: "How to" },
  ];

  const sideNavProps = {
    startNewChatCallback: handleNewChat,
    onChatSelect: handleChatSelect,
    onDeleteChat: handleDeleteChat,
    threads,
    loading,
    onClose: closeSidebar,
  };

  return (
    <div className="relative w-full h-[100dvh] flex overflow-hidden bg-white">
      {/* Mobile sidebar backdrop. Positioned `absolute` inside this
          non-scrolling 100dvh root (not `fixed`) — iOS Safari fails to repaint
          position:fixed overlays when the root has overflow clipping, leaving
          ghost/shaded bands. Absolute within a static container avoids that. */}
      <button
        type="button"
        aria-label="Close menu"
        tabIndex={sidebarVisible ? 0 : -1}
        className={`absolute inset-0 z-40 bg-black/40 md:hidden transition-opacity duration-300 ${
          sidebarVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeSidebar}
      />

      {/* Mobile slide-in drawer */}
      <div
        className={`absolute inset-y-0 left-0 z-50 w-[min(340px,88vw)] bg-[#FFFBF8] shadow-xl transform transition-transform duration-300 ease-out md:hidden ${
          sidebarVisible ? "translate-x-0" : "-translate-x-full pointer-events-none"
        }`}
        aria-hidden={!sidebarVisible}
      >
        <SideNav {...sideNavProps} />
      </div>

      {/* Desktop docked sidebar */}
      <div
        className={`hidden md:flex flex-col overflow-hidden transition-all duration-300 flex-shrink-0 bg-[#FFFBF8] ${
          sidebarVisible ? "w-[340px]" : "w-0"
        }`}
      >
        <SideNav {...sideNavProps} />
      </div>

      {/* Desktop sidebar edge — soft gradient instead of a hard border */}
      {sidebarVisible && (
        <div
          className="hidden md:block w-px shrink-0 self-stretch bg-gradient-to-b from-transparent via-orange-200/50 to-transparent shadow-[2px_0_12px_-4px_rgba(188,91,1,0.08)]"
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <div className="flex flex-col flex-grow min-w-0 relative overflow-x-hidden">
        {/* Sidebar toggle — hamburger on mobile; chevron on desktop (< collapse / > expand) */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarVisible ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={sidebarVisible}
          className="absolute top-3 left-3 z-40 p-2.5 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors md:top-4 md:left-4"
        >
          {/* Mobile: hamburger */}
          <svg
            className="w-6 h-6 text-gray-600 md:hidden"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
          {/* Desktop: chevron */}
          <svg
            className="hidden md:block w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={sidebarVisible ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
            />
          </svg>
        </button>

        {/* Header: tabs + account/login on one themed bar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <Navbar
            tabs={
              <div className="flex items-center gap-0.5 sm:gap-1 rounded-lg bg-white/90 p-0.5 sm:p-1 shadow-md backdrop-blur-sm overflow-x-auto max-w-[calc(100vw-7rem)] sm:max-w-none no-scrollbar">
                {navTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTabSelect(tab.key)}
                    className={`rounded-md px-2.5 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.key
                        ? "bg-[#BC5B01] text-white shadow-sm"
                        : "text-gray-600 hover:bg-orange-50 hover:text-[#BC5B01]"
                    }`}
                  >
                    <span className="sm:hidden">{tab.shortLabel}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            }
          />
        </div>

        {/* Main view */}
        {activeTab === "chat" && (
          <ChatBox
            newChat={newChat}
            selectedThreadId={selectedThreadId}
            setSelectedThreadId={setSelectedThreadId}
            addThread={addThread}
            threads={threads}
            user={user}
            generateTitleFromQuestion={getTitleFromQuestion} // Using simple truncation for now not AI generation
            savedDiscourses={savedDiscourses}
            onSaveDiscourse={handleSaveDiscourse}
            onUnsaveDiscourse={handleUnsaveDiscourse}
          />
        )}
        {activeTab === "collections" && (
          <div className="flex-grow overflow-hidden pt-[4.5rem] sm:pt-16">
            <CollectionsTab />
          </div>
        )}
        {activeTab === "browse" && (
          <div className="flex-grow overflow-hidden pt-[4.5rem] sm:pt-16">
            <BrowseTab />
          </div>
        )}
        {activeTab === "howto" && (
          <div className="flex-grow overflow-hidden pt-[4.5rem] sm:pt-16">
            <HowToTab />
          </div>
        )}
      </div>
    </div>
  );
};

export default Chatpage;